import React, { Suspense, useCallback, useMemo, useState } from 'react';
import { CaretUp } from '@nike/nike-design-system-icons';
import cx from 'classnames';
import { useQuery } from '@tanstack/react-query';
import { QUERY_KEYS } from '../../constants';
import { getRecommendNavFilter } from 'clientAPI/recommendnavv1';
import { useRevalidator } from 'react-router-dom';
import { Button, Checkbox } from '@nike/eds';

function useFilterData(input: FilterProps) {
  const revalidator = useRevalidator();

  const searchOptions: string[] = [];
  for (const option of Object.entries(input.searchOptions)) {
    if (option[1] && option[0] !== 'upmid') {
      searchOptions.push(`${option[0]}:${option[1]}`);
    }
  }

  const qs = new URLSearchParams({
    searchOptions: searchOptions.join(','),
    headerCallerId: input.callerId,
    headerUserAgent: input.userAgent,
  });
  const body = JSON.stringify({
    // seoDisabled: true,
    // status: 'ACTIVE',
    channelId: input.channelId,
    language: input.language,
    marketplace: input.marketplace,
    searchTerms: input.searchTerms || '',
    attributeIds: input.taxonomyIds,
  });
  return useQuery(
    [QUERY_KEYS.RECOMMEND_NAV_FILTER, qs.toString(), body],
    getRecommendNavFilter(),
    {
      suspense: true,
      keepPreviousData: true,
      onError: (err: keyable) => err.status === 401 && revalidator.revalidate(),
    },
  );
}

type FilterProps = {
  channelId: string;
  language: string;
  marketplace: string;
  searchTerms: string;
  taxonomyIds: string[];
  searchOptions: { [key: string]: string };
  onChange: (taxonomyIds: string[]) => void;
  callerId: string;
  userAgent: string;
};

export function Filter(props: FilterProps) {
  return (
    <nav>
      <Suspense fallback={null}>
        <FilterCore {...props} />
      </Suspense>
    </nav>
  );
}

export function FilterBreadcrumbs(props: FilterProps) {
  return (
    <Suspense fallback={null}>
      <FilterBreadcrumbsCore {...props} />
    </Suspense>
  );
}

function FilterBreadcrumbsCore(props: FilterProps) {
  const { data } = useFilterData(props);
  if (!data?.breadcrumbs) return null;

  const theirOnChange = props.onChange;

  return (
    <ul className='tw-flex tw-gap-1'>
      {data.breadcrumbs.map((breadcrumb, idx) => {
        return (
          <li key={breadcrumb.displayText} className='tw-flex tw-gap-1'>
            <Button
              variant='ghost'
              disableRipple
              onClick={() => theirOnChange(breadcrumb.navigationAttributeIds)}
            >
              {breadcrumb.displayText}
            </Button>
            {idx !== data.breadcrumbs.length - 1 ? <span>/</span> : null}
          </li>
        );
      })}
    </ul>
  );
}

function FilterCore(props: FilterProps) {
  const { data } = useFilterData(props);
  const theirOnChange = props.onChange;

  const handleCheckboxOnChange = useCallback(
    (option: RecNavFilterOption) => {
      // the backend API updates option.navigationAttributeIds based on current state
      // we don't need to construct it ourselves
      theirOnChange(option.navigationAttributeIds);
    },
    [theirOnChange],
  );

  return (
    <>
      {data?.categories && data?.categories.length > 0 ? (
        <ul>
          {data.categories.map((item) => {
            return (
              <li key={item.displayText} className='tw-my-1'>
                <Button
                  variant='ghost'
                  disableRipple
                  onClick={() => handleCheckboxOnChange(item)}
                  style={{
                    ...(item.selected
                      ? {
                          fontWeight: 'bold',
                        }
                      : {
                          fontWeight: 'unset',
                        }),
                  }}
                >
                  {item.displayText}
                </Button>
              </li>
            );
          })}
        </ul>
      ) : null}
      <ul>
        {data?.filters.map((filter) => {
          return (
            <li key={filter.displayText}>
              <Collapsible
                isOpen={true}
                label={filter.displayText}
                selectedCount={
                  filter.options.filter((item) => item.selected).length
                }
              >
                <CheckboxGroup
                  filter={filter}
                  handleCheckboxOnChange={handleCheckboxOnChange}
                />
              </Collapsible>
            </li>
          );
        })}
      </ul>
    </>
  );
}

function CheckboxGroup({
  filter,
  handleCheckboxOnChange,
}: {
  filter: RecNavFilter;
  handleCheckboxOnChange: (option: RecNavFilterOption, value: boolean) => void;
}) {
  const [showMore, setShowMore] = useState(
    !(
      typeof filter.showMoreAfter === 'number' &&
      filter.showMoreAfter > 0 &&
      filter.options.length > filter.showMoreAfter
    ),
  );

  const options = useMemo(() => {
    if (
      !showMore &&
      typeof filter.showMoreAfter === 'number' &&
      filter.showMoreAfter > 0
    ) {
      return filter.options.slice(0, filter.showMoreAfter);
    }
    return filter.options;
  }, [filter.options, filter.showMoreAfter, showMore]);

  // to debug selection state
  // useEffect(() => {
  //   if (filter.displayText === '性别') {
  //     filter.options.forEach((o) => {
  //       console.log(o.displayText, o.navigationAttributeIds);
  //     });
  //   }
  // }, [filter]);

  return (
    <div className='tw-flex tw-flex-col tw-items-start tw-py-2'>
      {filter.selectType === 'MULTI_SELECT_OR' ? (
        <>
          {options.map((o) => {
            return (
              <Checkbox
                key={o.attributeId}
                label={o.displayText}
                id={o.attributeId}
                checked={o.selected}
                onChange={(e) => handleCheckboxOnChange(o, e.target.checked)}
              />
            );
          })}
          {typeof filter.showMoreAfter === 'number' &&
          filter.showMoreAfter > 0 &&
          filter.options.length > filter.showMoreAfter ? (
            <div className='tw-mt-1'>
              {showMore ? (
                <GhostButton onClick={() => setShowMore(false)}>
                  - Less
                </GhostButton>
              ) : (
                <GhostButton onClick={() => setShowMore(true)}>
                  + More
                </GhostButton>
              )}
            </div>
          ) : null}
        </>
      ) : (
        <p>{filter.selectType + ' not handled ' + filter.displayText}</p>
      )}
    </div>
  );
}

function Collapsible(props: {
  children: React.ReactNode;
  selectedCount: number;
  isOpen: boolean;
  label: string;
}) {
  const [isOpen, setIsOpen] = useState(props.isOpen);
  const toggle = useCallback(() => {
    setIsOpen((isOpen) => !isOpen);
  }, []);
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      e.preventDefault();
      if (e.key === 'Enter' || e.key === ' ') {
        toggle();
      }
    },
    [toggle],
  );

  return (
    <div>
      <div
        aria-expanded={isOpen ? 'true' : 'false'}
        aria-label={props.label}
        role='button'
        tabIndex={0}
        className='tw-flex tw-justify-between tw-py-2 tw-pr-2'
        onClick={toggle}
        onKeyDown={handleKeyDown}
      >
        <div className='tw-flex'>
          <span className='tw-font-bold'>{props.label}</span>
          {props.selectedCount > 0 ? (
            <div className='tw-ml-1'>({props.selectedCount})</div>
          ) : null}
        </div>
        <div
          className={cx('tw-transition-transform', {
            'tw-rotate-0': isOpen,
            'tw-rotate-180': !isOpen,
          })}
        >
          <CaretUp />
        </div>
      </div>
      {isOpen ? props.children : null}
    </div>
  );
}

function GhostButton(props: Parameters<typeof Button<'button'>>[0]) {
  const { children, ...rest } = props;
  return (
    <Button
      variant='ghost'
      disableRipple
      style={{ fontWeight: 'unset ' }}
      {...rest}
    >
      {children}
    </Button>
  );
}
